import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { BackLink } from "@/components/brand/BackLink";
import { CalculatorDisclaimer } from "@/components/brand/CalculatorDisclaimer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  fmtUSD,
  emailShareLink,
  logCalculatorCompletion,
} from "@/lib/calculators";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export const Route = createFileRoute("/tools/compound")({
  head: () => ({
    meta: [
      { title: "Trust Fund Baby Calculator — Compound Growth | Currency Thoughts" },
      {
        name: "description",
        content:
          "What if every child in your family started life with a head start? See what a small monthly contribution becomes over a generation.",
      },
      { property: "og:title", content: "Trust Fund Baby — Compound Growth" },
      {
        property: "og:description",
        content:
          "A small monthly deposit, started at birth, becomes the foundation of generational wealth.",
      },
    ],
    links: [{ rel: "canonical", href: "/tools/compound" }],
  }),
  component: CompoundPage,
});

interface CompoundState {
  monthly: number;
  years: number;
  rate: number; // percent
  startingAge: number;
}

function projectSeries(s: CompoundState) {
  const r = s.rate / 100 / 12;
  const months = s.years * 12;
  let bal = 0;
  const out: { age: number; balance: number; contributed: number }[] = [];
  out.push({ age: s.startingAge, balance: 0, contributed: 0 });
  for (let m = 1; m <= months; m++) {
    bal = (bal + s.monthly) * (1 + r);
    if (m % 12 === 0) {
      const yr = m / 12;
      out.push({
        age: s.startingAge + yr,
        balance: bal,
        contributed: s.monthly * 12 * yr,
      });
    }
  }
  return out;
}

function CompoundPage() {
  const [done, setDone] = useState(false);
  const [s, setS] = useState<CompoundState>({
    monthly: 100,
    years: 25,
    rate: 7,
    startingAge: 0,
  });
  const series = useMemo(() => projectSeries(s), [s]);
  const final = series[series.length - 1];
  const growth = final.balance - final.contributed;

  async function onComplete() {
    const r = await logCalculatorCompletion(
      "compound",
      s,
      { final_balance: final.balance, contributed: final.contributed, growth },
    );
    if (r.saved) toast.success("Saved to your Vault");
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 pt-12 pb-24">
        <BackLink to="/" label="Back to home" />

        {!done ? (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
            <div>
              <Eyebrow>— Compound Growth · The Trust Fund Baby —</Eyebrow>
              <h1 className="mt-5 font-display text-[36px] md:text-[42px] leading-[1.08] text-navy-900">
                What if every child in your family started life with a head&nbsp;start?
              </h1>
              <p className="mt-5 font-sans text-[16px] leading-[1.65] text-charcoal-900 max-w-[58ch]">
                Wealth that lasts a generation rarely begins with a lump sum. It
                begins with a small, automatic deposit — started early, left
                alone, and quietly compounded.
              </p>

              <div className="mt-8 bg-white border border-bronze-600/25 rounded-[4px] p-7 shadow-soft">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <MoneyInput
                    label="Monthly contribution"
                    value={s.monthly}
                    onChange={(v) => setS({ ...s, monthly: v })}
                  />
                  <SliderField
                    label="Years invested"
                    value={s.years}
                    min={5}
                    max={50}
                    suffix=" yrs"
                    onChange={(v) => setS({ ...s, years: v })}
                  />
                  <SliderField
                    label="Assumed annual rate"
                    value={s.rate}
                    min={3}
                    max={10}
                    step={0.5}
                    suffix="%"
                    onChange={(v) => setS({ ...s, rate: v })}
                  />
                  <SliderField
                    label="Starting age"
                    value={s.startingAge}
                    min={0}
                    max={40}
                    suffix=" yrs"
                    onChange={(v) => setS({ ...s, startingAge: v })}
                  />
                </div>
                <div className="mt-7 flex justify-end">
                  <PrimaryButton onClick={onComplete} size="lg">
                    See the legacy →
                  </PrimaryButton>
                </div>
              </div>
            </div>

            {/* Live chart sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-navy-900 text-cream-50 rounded-[4px] p-6 shadow-soft">
                <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-400">
                  Projected value at age {final.age.toFixed(0)}
                </p>
                <p
                  className="mt-3 font-display text-[40px] leading-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {fmtUSD(final.balance)}
                </p>
                <div className="mt-6 h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series}>
                      <CartesianGrid stroke="rgba(201,168,118,0.12)" vertical={false} />
                      <XAxis dataKey="age" tick={{ fill: "#C9A876", fontSize: 10 }} stroke="rgba(201,168,118,0.3)" />
                      <YAxis tick={{ fill: "#C9A876", fontSize: 10 }} stroke="rgba(201,168,118,0.3)" tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                      <Tooltip
                        contentStyle={{ background: "#002D68", border: "1px solid #A88B5C", borderRadius: 2, color: "#F5F1E8" }}
                        formatter={(v: number) => fmtUSD(v)}
                      />
                      <Line type="monotone" dataKey="contributed" stroke="#C9A876" strokeWidth={1} dot={false} strokeDasharray="3 3" />
                      <Line type="monotone" dataKey="balance" stroke="#C9A876" strokeWidth={2.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-3 font-sans italic text-[11px] text-bronze-400/80">
                  Dashed: contributions. Solid: total value.
                </p>
              </div>
            </aside>
          </div>
        ) : (
          <Result s={s} series={series} final={final} growth={growth} onRestart={() => setDone(false)} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function MoneyInput({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">{label}</Label>
      <div className="mt-2 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display text-navy-800">$</span>
        <Input
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="pl-7 font-display text-[18px] text-navy-900 h-12"
        />
      </div>
    </div>
  );
}

function SliderField({
  label, value, min, max, step = 1, suffix = "", onChange,
}: { label: string; value: number; min: number; max: number; step?: number; suffix?: string; onChange: (n: number) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full accent-bronze-600"
        />
        <span className="font-display text-navy-900 text-[18px] min-w-[5ch] text-right">
          {value}
          {suffix}
        </span>
      </div>
    </div>
  );
}

function Result({
  s, series, final, growth, onRestart,
}: {
  s: CompoundState;
  series: { age: number; balance: number; contributed: number }[];
  final: { age: number; balance: number; contributed: number };
  growth: number;
  onRestart: () => void;
}) {
  const share = emailShareLink(
    "Our generational growth projection",
    `Currency Thoughts compound projection:\n\n$${s.monthly}/mo for ${s.years} years at ${s.rate}%\n  Contributed: ${fmtUSD(final.contributed)}\n  Growth: ${fmtUSD(growth)}\n  Final value at age ${final.age}: ${fmtUSD(final.balance)}\n\nSee the model: https://www.currencythoughts.net/tools/compound`,
  );
  return (
    <div className="mt-8">
      <div className="mx-auto max-w-3xl text-center">
        <Eyebrow align="center">— The Generational Number —</Eyebrow>
        <h1
          className="mt-6 font-display leading-[1] text-[64px] md:text-[96px]"
          style={{
            backgroundImage: "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {fmtUSD(final.balance)}
        </h1>
        <p className="mt-6 font-sans text-[15px] md:text-[16px] leading-[1.7] text-charcoal-900 max-w-[60ch] mx-auto">
          ${s.monthly} a month — about the cost of a streaming bundle — quietly
          compounds into a foundation. {fmtUSD(growth)} of this is growth the
          family never had to earn at a desk.
        </p>
      </div>

      <div className="mt-10 bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft max-w-4xl mx-auto h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series}>
            <CartesianGrid stroke="rgba(168,139,92,0.15)" vertical={false} />
            <XAxis dataKey="age" tick={{ fill: "#6B7280", fontSize: 11 }} stroke="rgba(168,139,92,0.3)" label={{ value: "Age", position: "insideBottom", offset: -4, fill: "#6B7280", fontSize: 11 }} />
            <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} stroke="rgba(168,139,92,0.3)" tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
            <Tooltip
              contentStyle={{ background: "#fff", border: "1px solid #A88B5C", borderRadius: 2 }}
              formatter={(v: number) => fmtUSD(v)}
            />
            <Line type="monotone" dataKey="contributed" stroke="#A88B5C" strokeWidth={1.5} dot={false} strokeDasharray="4 4" name="Contributed" />
            <Line type="monotone" dataKey="balance" stroke="#0A3369" strokeWidth={3} dot={false} name="Total value" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
        <Stat label="Contributed" value={fmtUSD(final.contributed)} />
        <Stat label="Compounded growth" value={fmtUSD(growth)} />
        <Stat label={`Final · age ${final.age.toFixed(0)}`} value={fmtUSD(final.balance)} />
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
        <PrimaryButton asChild size="lg">
          <a href="/book?source=compound">Build this for my family →</a>
        </PrimaryButton>
        <SecondaryButton asChild size="lg">
          <Link to="/tools/family-snapshot">Take the Family Snapshot →</Link>
        </SecondaryButton>
      </div>

      <div className="mt-10 max-w-3xl mx-auto text-center">
        <p className="font-sans text-[13px] text-charcoal-500">
          See the strategy behind the chart —{" "}
          <Link
            to="/academy/lessons/$lessonSlug"
            params={{ lessonSlug: "first-job-first-roth" }}
            className="text-bronze-600 hover:text-bronze-400 underline underline-offset-4"
          >
            Start early: the first Roth →
          </Link>
        </p>
        <div className="mt-5 flex items-center justify-center gap-3 text-[12px]">
          <a href={share} className="font-sans text-bronze-600 hover:text-bronze-400 underline underline-offset-4">Email this to my spouse</a>
          <span className="text-charcoal-500">·</span>
          <button onClick={onRestart} className="font-sans text-charcoal-500 hover:text-navy-800 underline underline-offset-4">Recalculate</button>
        </div>
      </div>

      <div className="mt-12 max-w-3xl mx-auto">
        <CalculatorDisclaimer />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-bronze-600/20 rounded-[4px] p-5 text-center shadow-soft">
      <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-charcoal-500">{label}</p>
      <p className="mt-3 font-display text-[22px] text-navy-900">{value}</p>
    </div>
  );
}